import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { gameGuideById } from "../gameGuides";
import type { PlayableRoomGameId } from "../roomGameCatalog";

const siteUrl = "https://topzle.com";
const defaultDescription =
  "Play social games with friends and family in one Topzle room with live chat, voice, video, and smart local bots.";

const publicPages: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Topzle — Play, talk, and stay together",
    description: defaultDescription,
  },
  "/games": {
    title: "Social games to play online | Topzle",
    description:
      "Explore Topzle games including Chess, Classic Indian Ludo, Tic Tac Toe, Battleship, Draw & Guess, Connect Four, and more.",
  },
  "/blog": {
    title: "Topzle Lab | Game bots, strategy, and experiments",
    description:
      "Read simple and mathematical explanations of Topzle game bots, reproducible match experiments, and disclosed Ludo dice rules.",
  },
  "/blog/bot-experiments": {
    title: "How Topzle tests game bots | Research method",
    description:
      "See Topzle's reproducible bot evaluation protocol, seat balancing, fixed seeds, game-specific metrics, and uncertainty intervals.",
  },
  "/blog/smart-ludo-dice": {
    title: "How Smart Dice works in Topzle Ludo",
    description:
      "See the disclosed Smart Dice probabilities, catch-up limits, tactical weighting, fair-dice fallback, and per-roll audit data.",
  },
  "/how-to-play": {
    title: "How to play Topzle games | Rules and guides",
    description:
      "Learn the rules, controls, goals, and useful strategies for every playable Topzle game.",
  },
  "/safety": {
    title: "Safety and family play | Topzle",
    description:
      "Learn how Topzle protects younger players and gives families clear controls for chat, voice, video, and reporting.",
  },
  "/about": {
    title: "About Topzle",
    description:
      "Topzle makes online game night feel easy, social, and close to the people you love.",
  },
  "/contact": {
    title: "Contact Topzle",
    description:
      "Contact the Topzle team for product help, safety concerns, or business enquiries.",
  },
  "/privacy": {
    title: "Privacy policy | Topzle",
    description: "Read how Topzle handles account, room, communication, and safety data.",
  },
  "/terms": {
    title: "Terms of service | Topzle",
    description: "Read the terms that apply when you use Topzle.",
  },
  "/cookies": {
    title: "Cookie policy | Topzle",
    description: "Learn how Topzle uses essential storage and cookies.",
  },
};

function setMeta(selector: string, attribute: string, value: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    const match = selector.match(/\[(property|name)="([^"]+)"\]/);
    if (match?.[1] && match[2]) element.setAttribute(match[1], match[2]);
    document.head.append(element);
  }
  element.setAttribute(attribute, value);
}

export function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const guideMatch = location.pathname.match(/^\/how-to-play\/([^/]+)$/);
    const guide = guideMatch?.[1]
      ? gameGuideById.get(guideMatch[1] as PlayableRoomGameId)
      : undefined;
    const botArticleMatch = location.pathname.match(/^\/blog\/(bots|experiments)\/([^/]+)$/);
    const botGameNames: Record<string, string> = {
      "tic-tac-toe": "Tic Tac Toe",
      chess: "Chess",
      ludo: "Classic Indian Ludo",
      "connect-four": "Connect 4",
      battleship: "Battleship",
      "draw-guess": "Draw & Guess",
      "rock-paper-scissors": "Rock Paper Scissors",
      "snakes-ladders": "Snakes & Ladders",
    };
    const botGameName = botArticleMatch?.[2] ? botGameNames[botArticleMatch[2]] : undefined;
    const measuredExperiments = new Set([
      "tic-tac-toe",
      "chess",
      "ludo",
      "connect-four",
      "battleship",
    ]);
    const requestedMeasuredExperiment =
      botArticleMatch?.[1] !== "experiments" ||
      (botArticleMatch[2] ? measuredExperiments.has(botArticleMatch[2]) : false);
    const botPage =
      botArticleMatch && botGameName && requestedMeasuredExperiment
        ? botArticleMatch[1] === "bots"
          ? {
              title: `How the ${botGameName} bot works | Topzle Lab`,
              description: `A plain-language and mathematical explanation of Topzle's ${botGameName} bot strategy and evaluation plan.`,
            }
          : {
              title: `${botGameName} bot experiment results | Topzle Lab`,
              description: `Reproducible ${botGameName} bot comparisons with fixed seeds, balanced seats, limitations, and uncertainty intervals.`,
            }
        : undefined;
    const page = guide
      ? {
          title: `How to play ${guide.name} | Rules and tips | Topzle`,
          description: `${guide.summary} Learn the goal, complete rules, controls, and useful tips.`,
        }
      : (botPage ?? publicPages[location.pathname]);
    const indexable = Boolean(page);
    const title = page?.title ?? "Topzle";
    const description = page?.description ?? defaultDescription;
    const canonicalPath = indexable ? location.pathname : "/";
    const canonicalUrl = `${siteUrl}${canonicalPath === "/" ? "/" : canonicalPath}`;

    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta(
      'meta[name="robots"]',
      "content",
      indexable ? "index,follow,max-image-preview:large" : "noindex,nofollow",
    );
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }
    canonical.href = canonicalUrl;

    const structuredData = guide
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HowTo",
              name: `How to play ${guide.name}`,
              description: guide.summary,
              url: canonicalUrl,
              step: guide.steps.map((text, index) => ({
                "@type": "HowToStep",
                position: index + 1,
                text,
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "How to play",
                  item: `${siteUrl}/how-to-play`,
                },
                { "@type": "ListItem", position: 3, name: guide.name, item: canonicalUrl },
              ],
            },
          ],
        }
      : location.pathname.startsWith("/blog") && page
        ? {
            "@context": "https://schema.org",
            "@type": location.pathname === "/blog" ? "Blog" : "Article",
            name: title,
            headline: title,
            description,
            url: canonicalUrl,
            publisher: {
              "@type": "Organization",
              name: "Topzle",
              url: `${siteUrl}/`,
              logo: { "@type": "ImageObject", url: `${siteUrl}/icon.png` },
            },
          }
        : {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": `${siteUrl}/#organization`,
                name: "Topzle",
                url: `${siteUrl}/`,
                logo: `${siteUrl}/icon.png`,
                email: "contact@topzle.com",
              },
              {
                "@type": "WebSite",
                "@id": `${siteUrl}/#website`,
                name: "Topzle",
                url: `${siteUrl}/`,
                publisher: { "@id": `${siteUrl}/#organization` },
              },
              {
                "@type": "SoftwareApplication",
                name: "Topzle",
                applicationCategory: "GameApplication",
                operatingSystem: "Web",
                url: `${siteUrl}/`,
                description: defaultDescription,
              },
            ],
          };
    let script = document.head.querySelector<HTMLScriptElement>("#topzle-structured-data");
    if (!script) {
      script = document.createElement("script");
      script.id = "topzle-structured-data";
      script.type = "application/ld+json";
      document.head.append(script);
    }
    script.text = JSON.stringify(structuredData);
  }, [location.pathname]);

  return null;
}
